/**
 * The banner for a document whose images are incomplete (§9.5).
 *
 * A `partial` or `missing` document opens anyway. Pages with an image render
 * normally; pages without show a placeholder -- because **the extracted data
 * is already there and is readable without the image.** Only the left pane
 * is waiting.
 *
 * If rendering is disabled the banner names the exact command instead of
 * offering a spinner that will never resolve.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { Readiness } from '@server/sql/readiness.ts'
import type { JobState } from '@server/media/render.ts'
import { api, keys } from '~/lib/api.ts'
import { Button } from '~/components/ui/primitives.tsx'
import { Command } from '~/components/ui/empty.tsx'
import { useT } from '~/i18n/index.tsx'
import { count, duration } from '~/i18n/format.ts'

export function RenderStatus({
  documentId, tier, onPageArrived,
}: {
  documentId: number
  tier: string
  /** Fired as each page's file appears, so the rail can un-grey it. */
  onPageArrived: (pageNo: number) => void
}) {
  const { t, locale } = useT()
  const qc = useQueryClient()
  const [job, setJob] = useState<JobState | null>(null)

  const { data: readiness } = useQuery({
    queryKey: keys.readiness(documentId, tier),
    queryFn: () => api<Readiness>(`/api/documents/${documentId}/readiness?tier=${tier}`),
    // Zero, because it is watching the filesystem (§13).
    staleTime: 0,
  })

  const start = useMutation({
    mutationFn: () =>
      api<JobState>('/api/render', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ documentId, tier }),
      }),
    onSuccess: (state) => setJob(state),
  })

  // --- the event stream -------------------------------------------------
  const esRef = useRef<EventSource | null>(null)
  useEffect(() => {
    if (!job || job.status === 'done' || job.status === 'error') return
    if (esRef.current) return

    const es = new EventSource(`/api/render/${documentId}/events?tier=${tier}`)
    esRef.current = es

    es.addEventListener('state', (e) => {
      const s = JSON.parse((e as MessageEvent).data) as JobState
      setJob(s)
      if (s.status === 'done' || s.status === 'error') {
        es.close()
        esRef.current = null
        void qc.invalidateQueries({ queryKey: keys.readiness(documentId, tier) })
        void qc.invalidateQueries({ queryKey: keys.document(documentId) })
      }
    })
    es.addEventListener('progress', (e) => {
      const p = JSON.parse((e as MessageEvent).data) as { done: number; total: number; etaMs: number | null }
      setJob((j) => (j ? { ...j, done: p.done, total: p.total, etaMs: p.etaMs } : j))
    })
    es.addEventListener('page', (e) => {
      const p = JSON.parse((e as MessageEvent).data) as { pageNo: number }
      onPageArrived(p.pageNo)
    })
    // The client gives up rather than reconnecting forever; the job's own
    // terminal state is what closes this normally.
    es.onerror = () => { es.close(); esRef.current = null }

    return () => { es.close(); esRef.current = null }
  }, [job, documentId, tier, qc, onPageArrived])

  if (!readiness || readiness.status === 'ready') return null

  const running = job?.status === 'queued' || job?.status === 'running'

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--warn)]/30 bg-[var(--warn-bg)] px-3 py-1.5">
      <span className="text-[length:var(--text-data)] text-[var(--text)]">
        {running
          ? t('viewer.imagePreparing')
          : t('viewer.noImage')}
      </span>

      <span className="tnum text-[length:var(--text-micro)] text-[var(--text-muted)]">
        {running && job
          ? `${count(job.done, locale)} / ${count(job.total, locale)}`
          : `${count(readiness.present, locale)} / ${count(readiness.expected, locale)}`}
        {running && job?.etaMs ? ` · ${duration(job.etaMs, locale)}` : ''}
      </span>

      {running && job && (
        <span className="h-1 w-32 overflow-hidden rounded-full bg-[var(--border)]">
          <span
            className="block h-full bg-[var(--accent)] transition-[width] duration-300"
            style={{ width: `${job.total ? (job.done / job.total) * 100 : 0}%` }}
          />
        </span>
      )}

      {job?.status === 'error' && (
        <span className="text-[length:var(--text-micro)] text-[var(--fail)]">{job.error}</span>
      )}

      <div className="flex-1" />

      {readiness.renderEnabled ? (
        <Button
          size="sm"
          disabled={running || start.isPending}
          onClick={() => start.mutate()}
        >
          {job?.status === 'error' ? t('action.retry') : t('viewer.imagePreparing')}
        </Button>
      ) : (
        <span className="flex items-center gap-2 text-[length:var(--text-micro)] text-[var(--text-muted)]">
          {t('viewer.imageCommand')}
          <Command>{readiness.command}</Command>
        </span>
      )}
    </div>
  )
}
